import type { BundleDefinition } from "@p0security/shared/types/bundle";
import type { RequestorRule } from "@p0security/shared/types/policy/types";
import {
  Drawer,
  Flex,
  Tag,
  Timeline,
  type TimelineItemProps,
  Typography,
} from "@p0security/ui";
import { MaxDurationTag } from "components/Jit/Policy/components/previews/ApprovalOptionsPreview";
import { ApprovalPreview } from "components/Jit/Policy/components/previews/ApprovalPreview";
import { RequestorPreview } from "components/Jit/Policy/components/previews/RequestorPreview";
import { useMemo } from "react";

import { BundleItemDisplay } from "./BundleItemDisplay";
import {
  HeaderRow,
  RequestorDetails,
  SectionLabel,
  TitleText,
} from "./common/DrawerPrimitives";
import { bundleItemKey } from "./useAccessBundles";

const defaultRequestor: RequestorRule = { type: "any" };

const buildTimelineItems = (bundle: BundleDefinition): TimelineItemProps[] => {
  const approvals = bundle.approval ?? [];
  const requestor = bundle.requestor ?? defaultRequestor;

  return [
    {
      color: "gray",
      children: (
        <Flex vertical gap={8}>
          <SectionLabel>Identities</SectionLabel>
          <RequestorPreview requestor={requestor} />
          <RequestorDetails requestor={requestor} />
        </Flex>
      ),
    },
    {
      color: "gray",
      children: (
        <Flex vertical gap={8}>
          <SectionLabel>Resources</SectionLabel>
          {bundle.items.length === 0 ? (
            <Typography.Text type="secondary">
              No resources configured
            </Typography.Text>
          ) : (
            <Flex vertical gap={4}>
              {bundle.items.map((item) => (
                <BundleItemDisplay key={bundleItemKey(item)} item={item} />
              ))}
            </Flex>
          )}
        </Flex>
      ),
    },
    {
      color: "gray",
      children: (
        <Flex vertical gap={8}>
          <SectionLabel>Actions</SectionLabel>
          {approvals.length === 0 ? (
            <Typography.Text type="secondary">
              No actions configured
            </Typography.Text>
          ) : (
            <Flex vertical gap={8}>
              {approvals.map((approval, i) => (
                <Flex vertical gap={4} key={i}>
                  <ApprovalPreview approval={approval} />
                  <MaxDurationTag approval={approval} />
                </Flex>
              ))}
            </Flex>
          )}
        </Flex>
      ),
    },
  ];
};

export const BundlePreviewDrawer: React.FC<{
  bundle: BundleDefinition | null;
  onClose: () => void;
}> = ({ bundle, onClose }) => {
  const timelineItems = useMemo(
    () => (bundle ? buildTimelineItems(bundle) : []),
    [bundle]
  );

  const displayName = bundle?.label?.trim() || "Untitled bundle";
  const isPlaceholder = !bundle?.label?.trim();

  const drawerTitle = (
    <HeaderRow>
      <TitleText isPlaceholder={isPlaceholder}>{displayName}</TitleText>
      {bundle?.disabled && <Tag color="orange">Disabled</Tag>}
    </HeaderRow>
  );

  return (
    <Drawer
      title={drawerTitle}
      width={480}
      open={!!bundle}
      onClose={onClose}
      destroyOnClose
    >
      {bundle && <Timeline items={timelineItems} />}
    </Drawer>
  );
};
